import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import { MemoryRouter } from 'react-router-dom'
import { Chart } from 'chart.js'
import { toPng } from 'html-to-image'

import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'

import { BlockRenderModeProvider } from '../blocks/renderMode'
import { SlideRenderer } from '../components/SlideRenderer'
import type { BlockView } from '../hooks/useResolvedBlocks'
import { STAGE_HEIGHT, STAGE_WIDTH } from '../layouts'
import type { DeckDefinition, ElementDescriptor } from '../types/presentation.types'

/**
 * Capture a deck's map and chart blocks as images, for a published snapshot
 * (MYD-508).
 *
 * A snapshot is viewed where there is no live data: a public link, a PDF,
 * PowerPoint. Maps cannot be redrawn there (no tiles may be loaded) and charts
 * must not be redrawn from data that has since changed, so each one is stored
 * as the image it was when published.
 *
 * ## How
 *
 * Each slide holding such a block is drawn OFF-SCREEN, at the stage's own
 * 1280x720, through the same SlideRenderer the presenter uses, in the
 * `capture` render mode. That mode puts maps on the street basemap (satellite
 * imagery is never stored) and keeps their WebGL drawing buffer so the canvas
 * can be read back. Chart.js animations are switched off for the duration, so
 * a chart is captured finished rather than half-grown.
 *
 * The block's whole element is captured, not just its canvas: a map's pins,
 * clusters and labels are HTML laid over the canvas, and would otherwise be
 * missing from the picture. Images are taken at 2x for a sharp projector or
 * print, map images are stamped with the OpenStreetMap attribution the tiles
 * require, and each is uploaded to Cloudinary.
 *
 * Off-screen rather than hidden: a hidden element has no size, and WebGL will
 * not draw into a canvas with no size.
 */

export type RasterKind = 'map' | 'chart'

export interface RasterProgress {
	done: number
	total: number
}

/** How long a map may take to settle before it is captured as it stands. */
const MAP_TIMEOUT_MS = 20_000
/** A last moment after the maps settle, for fonts and late layout. */
const SETTLE_MS = 400
const PIXEL_RATIO = 2
const ATTRIBUTION = '© OpenStreetMap contributors'

/** Every block of the deck that is captured as an image, with what it is. */
export const rasterTargets = (deck: DeckDefinition, elements: Record<string, ElementDescriptor>) =>
	deck.stories.flatMap(story =>
		story.slides
			.map(slide => ({
				slide,
				blocks: slide.blocks
					.map(block => ({ id: block.id, kind: elements[block.element]?.rasterize ?? null }))
					.filter((entry): entry is { id: string; kind: RasterKind } => entry.kind === 'map' || entry.kind === 'chart'),
			}))
			.filter(entry => entry.blocks.length > 0),
	)

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/** Until every map in `root` reports ready, or the timeout. */
const mapsSettled = async (root: HTMLElement) => {
	const started = Date.now()
	while (Date.now() - started < MAP_TIMEOUT_MS) {
		const maps = [...root.querySelectorAll('[data-map-ready]')]
		if (maps.every(map => map.getAttribute('data-map-ready') === 'true')) return
		await sleep(150)
	}
}

const loadImage = (src: string) =>
	new Promise<HTMLImageElement>((resolve, reject) => {
		const image = new Image()
		image.onload = () => resolve(image)
		image.onerror = reject
		image.src = src
	})

/** The attribution the map tiles require, in the bottom-right corner of the image. */
export const stampAttribution = async (dataUrl: string): Promise<string> => {
	const image = await loadImage(dataUrl)
	const canvas = document.createElement('canvas')
	canvas.width = image.width
	canvas.height = image.height
	const context = canvas.getContext('2d')
	if (!context) return dataUrl
	context.drawImage(image, 0, 0)
	const size = 11 * PIXEL_RATIO
	context.font = `${size}px sans-serif`
	const padding = 4 * PIXEL_RATIO
	const width = context.measureText(ATTRIBUTION).width + padding * 2
	const height = size + padding * 2
	context.fillStyle = 'rgba(255, 255, 255, 0.8)'
	context.fillRect(canvas.width - width, canvas.height - height, width, height)
	context.fillStyle = '#333'
	context.textBaseline = 'middle'
	context.fillText(ATTRIBUTION, canvas.width - width + padding, canvas.height - height / 2)
	return canvas.toDataURL('image/png')
}

const dataUrlToFile = async (dataUrl: string, name: string) => {
	const blob = await (await fetch(dataUrl)).blob()
	return new File([blob], name, { type: 'image/png' })
}

/** Capture one element as a PNG data URL, retrying without web fonts if they cannot be embedded. */
const capture = async (element: HTMLElement) => {
	const options = { pixelRatio: PIXEL_RATIO, cacheBust: true, imagePlaceholder: undefined as string | undefined }
	try {
		return await toPng(element, options)
	} catch {
		// Embedding cross-origin font CSS is the usual failure; the text still
		// draws with the page's fallback fonts.
		return toPng(element, { ...options, skipFonts: true })
	}
}

export interface RasterizeOptions {
	deck: DeckDefinition
	/** The deck's blocks, resolved as the owner. */
	blocks: Record<string, BlockView | undefined>
	elements: Record<string, ElementDescriptor>
	theme?: string
	onProgress?: (progress: RasterProgress) => void
	/** Injectable for tests. */
	upload?: (file: File) => Promise<string>
}

const defaultUpload = async (file: File) => (await uploadToCloudinary(file, { folder: 'presentations/snapshots', resourceType: 'image' })).secure_url

/**
 * Capture and upload every map and chart block. Returns block id -> image URL.
 * A block that fails to capture is left out, rather than failing the publish;
 * the snapshot then shows its placeholder, and `failed` names it.
 */
export async function rasterizeDeck({ deck, blocks, elements, theme, onProgress, upload = defaultUpload }: RasterizeOptions): Promise<{ rasters: Record<string, string>; failed: string[] }> {
	const targets = rasterTargets(deck, elements)
	const total = targets.reduce((count, target) => count + target.blocks.length, 0)
	const rasters: Record<string, string> = {}
	const failed: string[] = []
	let done = 0
	onProgress?.({ done, total })
	if (total === 0) return { rasters, failed }

	const host = document.createElement('div')
	host.setAttribute('aria-hidden', 'true')
	Object.assign(host.style, { position: 'fixed', left: '-20000px', top: '0', width: `${STAGE_WIDTH}px`, height: `${STAGE_HEIGHT}px`, pointerEvents: 'none' })
	document.body.appendChild(host)
	const root = createRoot(host)

	const animation = Chart.defaults.animation
	Chart.defaults.animation = false
	try {
		for (const target of targets) {
			root.render(
				createElement(MemoryRouter, null, createElement(BlockRenderModeProvider, { value: 'capture' }, createElement(SlideRenderer, { slide: target.slide, blocks, theme }))),
			)
			await sleep(50)
			await mapsSettled(host)
			await sleep(SETTLE_MS)

			for (const { id, kind } of target.blocks) {
				try {
					const element = host.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(id)}"]`)
					// Only a block that drew: a placeholder ("Restricted") is not a picture of anything.
					if (!element || blocks[id]?.status !== 'ok') throw new Error('not drawn')
					const png = await capture(element)
					const stamped = kind === 'map' ? await stampAttribution(png) : png
					rasters[id] = await upload(await dataUrlToFile(stamped, `snapshot-${id}.png`))
				} catch {
					failed.push(id)
				}
				done += 1
				onProgress?.({ done, total })
			}
		}
	} finally {
		Chart.defaults.animation = animation
		root.unmount()
		host.remove()
	}
	return { rasters, failed }
}
